import type { Script, Beat } from '../script/schema';
import { playVfx } from './vfx';
import { Game, type GameState } from '../engine/game';
import { typewrite, type TypewriterController } from './typewriter';
import { autosave } from '../engine/save-manager';
import { renderSaveLoadModal } from './save-load-modal';
import { renderSceneSelectModal } from './scene-select';
import { AudioEngine } from '../engine/audio';
import {
  moodToBgmPath,
  resolveSceneMood,
  type SceneMood,
} from '../engine/scene-mood';
import { Parallax, type ParallaxLayer } from './parallax';
import { loadSettings, renderSettingsPanel, type VnSettings } from './settings';
import { BacklogStore, renderBacklogPanel } from './backlog';
import { preloadUpcoming } from '../engine/preloader';

export const BACKGROUND_LAYER_PRESENTATION = {
  midgroundOpacity: 0.38,
  foregroundOpacity: 0.14,
} as const;

function layerOpacity(layer: keyof typeof BACKGROUND_LAYER_PRESENTATION): string {
  return String(BACKGROUND_LAYER_PRESENTATION[layer]);
}

type SceneAtmosphere = 'fraying' | 'memory' | 'night' | 'interior' | 'neutral';

function atmosphereForBackground(bgPath: string): SceneAtmosphere {
  const slug = bgSlug(bgPath);

  if (slug.includes('memory') || slug === 'black') return 'memory';
  if (slug.includes('night') || slug.includes('dark')) return 'night';
  // Fraying / Threshold locations — drained, cool, absent thread-light
  if (
    slug.includes('faded-square') ||
    slug.includes('wicks-library') ||
    slug.includes('threshold') ||
    slug.includes('fraying')
  ) {
    return 'fraying';
  }
  if (
    slug.includes('archive') ||
    slug.includes('vault') ||
    slug.includes('lamplit') ||
    slug.includes('room') ||
    slug.includes('snug') ||
    slug.includes('library')
  ) {
    return 'interior';
  }

  return 'neutral';
}

/**
 * Probe whether an image URL exists (returns 200).
 * Uses a hidden Image element to avoid CORS issues with fetch.
 */
function probeImage(src: string): Promise<boolean> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = src;
  });
}

/**
 * Derive the background slug from a bg path.
 * e.g. "assets/bg/classroom.webp" → "classroom"
 *      "assets/bg/classroom-bg.webp" → "classroom"
 */
function bgSlug(bgPath: string): string {
  const filename = bgPath.split('/').pop() ?? bgPath;
  const name = filename.replace(/\.[^.]+$/, ''); // strip extension
  // If it already ends with -bg, strip that suffix to get the base slug
  return name.replace(/-bg$/, '');
}

/**
 * Given a bg path from the script, derive the directory prefix.
 * e.g. "assets/bg/classroom.webp" → "assets/bg/"
 */
function bgDir(bgPath: string): string {
  const lastSlash = bgPath.lastIndexOf('/');
  return lastSlash >= 0 ? bgPath.substring(0, lastSlash + 1) : '';
}

interface PlateSet {
  far: string | null;
  mid: string | null;
  fg: string | null;
  legacy: string | null;
}

/**
 * Probe for 3-layer plates, falling back to legacy single image.
 * Probe order: try <slug>-bg.webp first; if found, also try -mid + -fg.
 * Else fall back to legacy <slug>.webp.
 */
async function probePlates(bgPath: string): Promise<PlateSet> {
  const dir = bgDir(bgPath);
  const slug = bgSlug(bgPath);

  // Probe both .webp and .png plates — generated assets currently ship as .png,
  // but the parallax pipeline may emit .webp in the future. Try .webp first
  // (smaller / preferred), then .png as fallback.
  for (const ext of ['webp', 'png'] as const) {
    const farUrl = `${dir}${slug}-bg.${ext}`;
    const midUrl = `${dir}${slug}-mid.${ext}`;
    const fgUrl = `${dir}${slug}-fg.${ext}`;

    const farExists = await probeImage(`./${farUrl}`);
    if (farExists) {
      const [midExists, fgExists] = await Promise.all([
        probeImage(`./${midUrl}`),
        probeImage(`./${fgUrl}`),
      ]);
      return {
        far: farUrl,
        mid: midExists ? midUrl : null,
        fg: fgExists ? fgUrl : null,
        legacy: null,
      };
    }
  }

  // Fall back to the legacy single image (the path as authored in the script).
  return {
    far: null,
    mid: null,
    fg: null,
    legacy: bgPath,
  };
}

/**
 * ScenePlayer mounts the VN stage into #app and drives the game loop.
 */
export class ScenePlayer {
  private game: Game;
  private stage!: HTMLDivElement;

  // 3-layer parallax background elements
  private bgLayer!: HTMLDivElement;
  private bgFarImg!: HTMLImageElement;
  private midLayer!: HTMLDivElement;
  private bgMidImg!: HTMLImageElement;
  private fgLayer!: HTMLDivElement;
  private bgFgImg!: HTMLImageElement;

  private portraitImg!: HTMLImageElement;
  private speakerEl!: HTMLDivElement;
  private textEl!: HTMLDivElement;
  private continueEl!: HTMLDivElement;
  private dialogueEl!: HTMLDivElement;
  private portraitLayer!: HTMLDivElement;
  private menuBar!: HTMLDivElement;
  private backBtn!: HTMLButtonElement;
  private bgmIndicator!: HTMLDivElement;
  private bgmIndicatorTimer: ReturnType<typeof setTimeout> | null = null;
  private statusBar!: HTMLDivElement;
  private typewriter: TypewriterController | null = null;
  private waitingForInput = false;
  private choiceActive = false;
  private choiceFocusIndex = 0;
  private onEnd: ((epilogue: string) => void) | null;
  private onReturnToTitle: (() => void) | null;
  private keyHandler: ((e: KeyboardEvent) => void) | null = null;
  private keyUpHandler: ((e: KeyboardEvent) => void) | null = null;
  private audioInitialised = false;
  private backlog = new BacklogStore();
  private backlogOpen = false;
  private modalOpen = false;

  // Skip & auto-advance
  private skipActive = false;
  private autoActive = false;
  private autoTimer: ReturnType<typeof setTimeout> | null = null;
  private skipTimer: ReturnType<typeof setTimeout> | null = null;
  /** Timestamp (ms) before which manual advance input is ignored — prevents accidental skips */
  private inputLockUntil = 0;
  /** Set of beat IDs that have been displayed this session (for skip-read-only). */
  private readBeats = new Set<string>();

  // Parallax
  private parallax: Parallax | null = null;
  private settings: VnSettings;
  private localeChangeHandler: ((e: Event) => void) | null = null;
  private burgerCloseHandler: ((e: MouseEvent) => void) | null = null;
  private sceneSelectOpen = false;
  private returnPill: HTMLButtonElement | null = null;

  // Transition overlay
  private transitionOverlay!: HTMLDivElement;

  // ARIA live region for screen readers
  private ariaLive!: HTMLDivElement;

  constructor(
    private script: Script,
    private root: HTMLElement,
    onEnd?: (epilogue: string) => void,
    onReturnToTitle?: () => void,
    savedState?: GameState,
  ) {
    this.game = new Game(script);
    this.onEnd = onEnd ?? null;
    this.onReturnToTitle = onReturnToTitle ?? null;
    this.settings = loadSettings();

    if (savedState) {
      this.game.restoreState(savedState);
      // Mark all beats in history as read
      for (const id of savedState.history) {
        this.readBeats.add(id);
      }
    }

    this.game.setLocale(this.settings.locale ?? 'en');

    this.mount();
    this.bindInput();

    // If restoring a saved state, replay the most recent scene beat
    // so the background and BGM are set correctly.
    if (savedState) {
      this.restoreSceneContext();
    }

    this.processCurrentBeat();
  }

  /** Clean up event listeners and timers. */
  destroy(): void {
    if (this.keyHandler) {
      document.removeEventListener('keydown', this.keyHandler);
      this.keyHandler = null;
    }
    if (this.keyUpHandler) {
      document.removeEventListener('keyup', this.keyUpHandler);
      this.keyUpHandler = null;
    }
    this.typewriter?.cancel();
    this.parallax?.destroy();
    this.clearAutoTimer();
    this.clearSkipTimer();
    if (this.localeChangeHandler) {
      document.removeEventListener('vn-locale-change', this.localeChangeHandler);
      this.localeChangeHandler = null;
    }
    if (this.burgerCloseHandler) {
      document.removeEventListener('click', this.burgerCloseHandler);
      this.burgerCloseHandler = null;
    }
    this.returnPill = null;
  }

  /** Build the DOM structure and append to root. */
  private mount(): void {
    this.root.innerHTML = '';

    this.stage = document.createElement('div');
    this.stage.id = 'stage';
    this.stage.setAttribute('role', 'application');
    this.stage.setAttribute('aria-label', 'Visual novel stage');

    // ── BG far layer (depth=0.05) ──
    this.bgLayer = document.createElement('div');
    this.bgLayer.id = 'bg-layer';
    this.bgLayer.dataset['depth'] = '0.05';
    this.bgLayer.setAttribute('aria-hidden', 'true');
    this.bgFarImg = document.createElement('img');
    this.bgFarImg.id = 'bg-far';
    this.bgFarImg.alt = '';
    this.bgLayer.appendChild(this.bgFarImg);
    this.stage.appendChild(this.bgLayer);

    // ── Mid layer (depth=0.15) ──
    this.midLayer = document.createElement('div');
    this.midLayer.id = 'mid-layer';
    this.midLayer.dataset['depth'] = '0.15';
    this.midLayer.setAttribute('aria-hidden', 'true');
    this.bgMidImg = document.createElement('img');
    this.bgMidImg.id = 'bg-mid';
    this.bgMidImg.alt = '';
    this.midLayer.appendChild(this.bgMidImg);
    this.stage.appendChild(this.midLayer);

    // ── BG dim overlay (between parallax layers and portrait) ──
    const bgDim = document.createElement('div');
    bgDim.id = 'bg-dim';
    this.stage.appendChild(bgDim);

    // ── Beat ID pill (bottom-right, always visible, tap to copy) ──
    const beatPill = document.createElement('div');
    beatPill.id = 'beat-id-pill';
    beatPill.title = 'Tap to copy beat ID';
    beatPill.setAttribute('aria-label', 'Beat ID — tap to copy');
    beatPill.addEventListener('click', () => {
      const id = beatPill.textContent ?? '';
      if (!id) return;
      navigator.clipboard.writeText(id).then(() => {
        const prev = beatPill.textContent;
        beatPill.textContent = '✓ copied';
        beatPill.style.background = 'rgba(60,180,80,0.85)';
        setTimeout(() => {
          beatPill.textContent = prev;
          beatPill.style.background = '';
        }, 1200);
      });
    });
    this.stage.appendChild(beatPill);

    // ── Portrait layer (between mid and fg) ──
    this.portraitLayer = document.createElement('div');
    this.portraitLayer.id = 'portrait-layer';
    this.portraitLayer.setAttribute('aria-hidden', 'true');
    this.portraitImg = document.createElement('img');
    this.portraitImg.id = 'portrait';
    this.portraitImg.alt = '';
    this.portraitLayer.appendChild(this.portraitImg);
    this.stage.appendChild(this.portraitLayer);

    // ── FG layer (depth=0.30) ──
    this.fgLayer = document.createElement('div');
    this.fgLayer.id = 'fg-layer';
    this.fgLayer.dataset['depth'] = '0.30';
    this.fgLayer.setAttribute('aria-hidden', 'true');
    this.bgFgImg = document.createElement('img');
    this.bgFgImg.id = 'bg-fg';
    this.bgFgImg.alt = '';
    this.fgLayer.appendChild(this.bgFgImg);
    this.stage.appendChild(this.fgLayer);

    // ── Transition overlay (for dip-to-black, fade) ──
    this.transitionOverlay = document.createElement('div');
    this.transitionOverlay.id = 'transition-overlay';
    this.transitionOverlay.setAttribute('aria-hidden', 'true');
    this.stage.appendChild(this.transitionOverlay);

    // ── Menu bar ──
    this.menuBar = document.createElement('div');
    this.menuBar.id = 'menu-bar';
    this.menuBar.setAttribute('role', 'toolbar');
    this.menuBar.setAttribute('aria-label', 'Game controls');

    const backBtn = document.createElement('button');
    backBtn.className = 'menu-btn';
    backBtn.id = 'back-btn';
    backBtn.textContent = '← Back';
    backBtn.setAttribute('aria-label', 'Go back one line');
    backBtn.disabled = true;
    backBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.initAudio();
      void AudioEngine.getInstance().playSfx('./assets/audio/sfx/ui-click.mp3');
      this.handleGoBack();
    });
    this.backBtn = backBtn;

    const saveBtn = this.createMenuButton('Save', () => this.openSaveModal());
    const loadBtn = this.createMenuButton('Load', () => this.openLoadModal());
    const settingsBtn = this.createMenuButton('Settings', () => this.openSettings());
    const backlogBtn = this.createMenuButton('Log', () => this.openBacklog());
    const scenesBtn = this.createMenuButton('Scenes', () => this.openSceneSelect());

    // Skip button (toggle)
    const skipBtn = document.createElement('button');
    skipBtn.className = 'menu-btn';
    skipBtn.id = 'skip-btn';
    skipBtn.textContent = 'Skip';
    skipBtn.setAttribute('aria-pressed', 'false');
    skipBtn.setAttribute('aria-label', 'Toggle skip mode');
    skipBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.initAudio();
      void AudioEngine.getInstance().playSfx('./assets/audio/sfx/ui-click.mp3');
      this.toggleSkip();
    });

    // Auto button (toggle)
    const autoBtn = document.createElement('button');
    autoBtn.className = 'menu-btn';
    autoBtn.id = 'auto-btn';
    autoBtn.textContent = 'Auto';
    autoBtn.setAttribute('aria-pressed', 'false');
    autoBtn.setAttribute('aria-label', 'Toggle auto-advance mode');
    autoBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.initAudio();
      void AudioEngine.getInstance().playSfx('./assets/audio/sfx/ui-click.mp3');
      this.toggleAuto();
    });

    const titleBtn = this.createMenuButton('Title', () => this.returnToTitle());

    this.menuBar.appendChild(this.backBtn);
    this.menuBar.appendChild(saveBtn);
    this.menuBar.appendChild(loadBtn);
    this.menuBar.appendChild(settingsBtn);
    this.menuBar.appendChild(backlogBtn);
    this.menuBar.appendChild(scenesBtn);
    this.menuBar.appendChild(skipBtn);
    this.menuBar.appendChild(autoBtn);
    this.menuBar.appendChild(titleBtn);

    // ── Burger button (mobile only — hidden via CSS on desktop) ──
    const burgerBtn = document.createElement('button');
    burgerBtn.id = 'burger-btn';
    burgerBtn.setAttribute('aria-label', 'Menu');
    burgerBtn.setAttribute('aria-expanded', 'false');
    burgerBtn.innerHTML = '☰';
    // NOTE: do NOT set `display` inline — inline styles can't be overridden by
    // media queries without `!important`. Visibility is controlled entirely by
    // the `#burger-btn` CSS rules (hidden on desktop, shown ≤ 520px).
    burgerBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      const open = this.menuBar.classList.toggle('burger-open');
      burgerBtn.setAttribute('aria-expanded', String(open));
      burgerBtn.innerHTML = open ? '✕' : '☰';
    });
    // Close burger when clicking outside both the menu-bar and the burger
    // itself — stored so destroy() can remove it. (The burger now sits on the
    // stage, not inside the menu-bar, so we must check both nodes.)
    this.burgerCloseHandler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!this.menuBar.contains(target) && !burgerBtn.contains(target)) {
        this.menuBar.classList.remove('burger-open');
        burgerBtn.setAttribute('aria-expanded', 'false');
        burgerBtn.innerHTML = '☰';
      }
    };
    document.addEventListener('click', this.burgerCloseHandler);
    // Close burger when any menu button is clicked
    this.menuBar.querySelectorAll('.menu-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        this.menuBar.classList.remove('burger-open');
        burgerBtn.setAttribute('aria-expanded', 'false');
        burgerBtn.innerHTML = '☰';
      });
    });
    // Burger lives on the stage (not inside #menu-bar) so it can anchor to the
    // stage's top-left corner and remain tappable even when the menu-bar
    // collapses to a tiny right-anchored strip on mobile.
    this.stage.appendChild(this.menuBar);
    this.stage.appendChild(burgerBtn);

    // ── Status bar (shows Skip/Auto indicators) ──
    this.statusBar = document.createElement('div');
    this.statusBar.id = 'status-bar';
    this.statusBar.setAttribute('aria-live', 'polite');
    this.statusBar.setAttribute('aria-atomic', 'true');
    this.stage.appendChild(this.statusBar);

    // ── Dialogue box ──
    this.dialogueEl = document.createElement('div');
    this.dialogueEl.id = 'dialogue';
    this.dialogueEl.setAttribute('role', 'log');
    this.dialogueEl.setAttribute('aria-label', 'Dialogue');
    this.dialogueEl.setAttribute('aria-live', 'polite');

    this.speakerEl = document.createElement('div');
    this.speakerEl.id = 'speaker';
    this.speakerEl.setAttribute('aria-label', 'Speaker');
    this.dialogueEl.appendChild(this.speakerEl);

    this.textEl = document.createElement('div');
    this.textEl.id = 'text';
    this.textEl.setAttribute('aria-label', 'Dialogue text');
    this.dialogueEl.appendChild(this.textEl);

    this.continueEl = document.createElement('div');
    this.continueEl.id = 'continue-indicator';
    this.continueEl.textContent = '▾';
    this.continueEl.setAttribute('aria-hidden', 'true');
    this.dialogueEl.appendChild(this.continueEl);

    this.stage.appendChild(this.dialogueEl);

    // ── ARIA live region (visually hidden, for screen readers) ──
    this.ariaLive = document.createElement('div');
    this.ariaLive.id = 'aria-live-region';
    this.ariaLive.className = 'sr-only';
    this.ariaLive.setAttribute('role', 'status');
    this.ariaLive.setAttribute('aria-live', 'assertive');
    this.ariaLive.setAttribute('aria-atomic', 'true');
    this.stage.appendChild(this.ariaLive);

    this.root.appendChild(this.stage);

    // ── BGM indicator (bottom-left, always visible during play) ──
    this.bgmIndicator = document.createElement('div');
    this.bgmIndicator.id = 'bgm-indicator';
    this.bgmIndicator.setAttribute('aria-live', 'polite');
    this.bgmIndicator.setAttribute('aria-label', 'Now playing');
    this.stage.appendChild(this.bgmIndicator);

    // ── Initialise parallax ──
    this.initParallax();

    // ── Return-to-latest pill ──
    this.createReturnPill();
  }

  /** Create a menu bar button with click handler. */
  private createMenuButton(label: string, onClick: () => void): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.className = 'menu-btn';
    btn.textContent = label;
    btn.setAttribute('aria-label', label);
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.initAudio();
      void AudioEngine.getInstance().playSfx('./assets/audio/sfx/ui-click.mp3');
      onClick();
    });
    return btn;
  }

  /** Last hover SFX timestamp — debounce so rapid mouse moves don't spam. */
  private lastHoverSfxAt = 0;

  /** Play a soft hover click SFX with debounce (min 80ms between plays). */
  private playHoverSfx(): void {
    const now = performance.now();
    if (now - this.lastHoverSfxAt < 80) return;
    this.lastHoverSfxAt = now;
    void AudioEngine.getInstance().playSfx('./assets/audio/sfx/ui-click.mp3');
  }

  /** Set up the parallax controller with the 3 background layers. */
  private initParallax(): void {
    const layers: ParallaxLayer[] = [
      { el: this.bgLayer, depth: 0.05 },
      { el: this.midLayer, depth: 0.15 },
      { el: this.fgLayer, depth: 0.30 },
    ];
    this.parallax = new Parallax(this.stage, layers);
    this.parallax.setEnabled(this.settings.parallaxEnabled ?? true);
    this.parallax.start();
  }

  /** Bind click / keyboard input. */
  private bindInput(): void {
    this.stage.addEventListener('click', () => {
      this.initAudio();
      this.handleInput();
    });

    // Scroll-up on dialogue box opens backlog
    this.dialogueEl.addEventListener('wheel', (e) => {
      if (e.deltaY < 0 && !this.backlogOpen && !this.modalOpen) {
        e.preventDefault();
        this.openBacklog();
      }
    }, { passive: false });

    // Keyboard handler — supports dialogue advance, choice navigation, skip, auto, backlog
    this.keyHandler = (e: KeyboardEvent) => {
      this.initAudio();

      // Esc closes any open modal
      if (e.key === 'Escape') {
        if (this.modalOpen || this.backlogOpen) {
          // The modal's own Esc handler will close it
          return;
        }
      }

      // ArrowLeft / H goes back one beat (when no modal is open and not in choice mode)
      if ((e.key === 'ArrowLeft' || e.key === 'h' || e.key === 'H') && !this.choiceActive && !this.modalOpen && !this.backlogOpen) {
        e.preventDefault();
        this.handleGoBack();
        return;
      }

      // B opens backlog (when no modal is open and not in choice mode)
      if ((e.key === 'b' || e.key === 'B') && !this.choiceActive && !this.modalOpen && !this.backlogOpen) {
        e.preventDefault();
        this.openBacklog();
        return;
      }
      if ((e.key === 's' || e.key === 'S') && !this.choiceActive && !this.modalOpen && !this.backlogOpen && !this.sceneSelectOpen) {
        e.preventDefault();
        this.openSceneSelect();
        return;
      }

      // A toggles auto-advance
      if ((e.key === 'a' || e.key === 'A') && !this.choiceActive && !this.modalOpen && !this.backlogOpen) {
        e.preventDefault();
        this.toggleAuto();
        return;
      }

      // Ctrl held = skip mode (hold-to-skip)
      if (e.key === 'Control' && !this.modalOpen && !this.backlogOpen) {
        if (!this.skipActive) {
          this.setSkip(true);
        }
        return;
      }

      // S key toggles skip (when not in choice mode)
      // Note: we use 's' only when not in choice mode (where it's used for navigation)
      // So we use 'Tab' as an alternative skip toggle
      if (e.key === 'Tab' && !this.modalOpen && !this.backlogOpen) {
        e.preventDefault();
        this.toggleSkip();
        return;
      }

      // Don't process game input while a modal is open
      if (this.modalOpen || this.backlogOpen) return;

      if (this.choiceActive) {
        this.handleChoiceKey(e);
        return;
      }
      if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        this.handleInput();
      }
    };

    // Ctrl release stops hold-to-skip
    this.keyUpHandler = (e: KeyboardEvent): void => {
      if (e.key === 'Control' && this.skipActive) {
        this.setSkip(false);
      }
    };

    document.addEventListener('keydown', this.keyHandler);
    document.addEventListener('keyup', this.keyUpHandler);

    // Re-render current beat when locale changes
    this.localeChangeHandler = (e: Event) => {
      const detail = (e as CustomEvent<{ locale: import('../engine/game').Locale }>).detail;
      this.game.setLocale(detail.locale);
      const beat = this.game.current();
      if (beat.kind === 'say' || beat.kind === 'choice' || beat.kind === 'end') {
        this.processCurrentBeat();
      }
    };
    document.addEventListener('vn-locale-change', this.localeChangeHandler);
  }

  /**
   * Initialise the AudioContext on first user gesture.
   * Browsers require a user interaction before audio can play.
   */
  private initAudio(): void {
    if (this.audioInitialised) return;
    this.audioInitialised = true;
    // Ensure context on first user gesture, then replay BGM for current scene.
    // The AudioContext is suspended until a gesture occurs; without this the
    // BGM that was scheduled during processCurrentBeat() silently fails.
    void AudioEngine.getInstance().ensureContext().then(() => {
      this.replayCurrentSceneBgm();
    });
  }

  /**
   * Re-trigger BGM for whichever background is currently displayed.
   * Called after the AudioContext is unlocked on first user gesture.
   */
  private replayCurrentSceneBgm(): void {
    const src = this.bgFarImg?.src;
    if (!src) return;
    // Convert absolute URL → relative asset path the atmosphere resolver expects
    try {
      const url = new URL(src);
      // e.g. "/assets/bg/lethe-archive-bg.webp" → "assets/bg/lethe-archive-bg.webp"
      const relativePath = url.pathname.replace(/^\//, '');
      this.applySceneAtmosphere(relativePath);
    } catch {
      // If src is already relative or empty, skip gracefully
    }
  }

  // ── Skip mode ──

  private toggleSkip(): void {
    this.setSkip(!this.skipActive);
  }

  private setSkip(active: boolean): void {
    // Disable auto when skip is activated
    if (active && this.autoActive) {
      this.setAuto(false);
    }

    this.skipActive = active;
    this.updateStatusBar();
    this.updateToggleButtons();

    if (active && this.waitingForInput) {
      this.doSkipAdvance();
    }
  }

  private doSkipAdvance(): void {
    if (!this.skipActive || this.choiceActive || this.modalOpen || this.backlogOpen) return;

    const beat = this.game.current();
    const settings = loadSettings();

    // If skip-read-only is enabled, only skip beats we've already seen
    if (settings.skipReadOnly && !this.readBeats.has(beat.id)) {
      this.setSkip(false);
      return;
    }

    // Complete typewriter immediately if still revealing
    if (this.typewriter?.isRevealing()) {
      this.typewriter.complete();
    }

    // Schedule next advance with a short delay for readability
    this.clearSkipTimer();
    this.skipTimer = setTimeout(() => {
      if (!this.skipActive || !this.waitingForInput) return;
      this.waitingForInput = false;
      this.hideContinueIndicator();
      const next = this.game.advance();
      if (next) {
        this.processCurrentBeat();
      }
    }, 50);
  }

  private clearSkipTimer(): void {
    if (this.skipTimer !== null) {
      clearTimeout(this.skipTimer);
      this.skipTimer = null;
    }
  }

  // ── Auto-advance mode ──

  private toggleAuto(): void {
    this.setAuto(!this.autoActive);
  }

  private setAuto(active: boolean): void {
    // Disable skip when auto is activated
    if (active && this.skipActive) {
      this.setSkip(false);
    }

    this.autoActive = active;
    this.updateStatusBar();
    this.updateToggleButtons();

    if (active && this.waitingForInput) {
      this.scheduleAutoAdvance();
    }
  }

  private scheduleAutoAdvance(): void {
    this.clearAutoTimer();
    if (!this.autoActive || this.choiceActive) return;

    const settings = loadSettings();
    this.autoTimer = setTimeout(() => {
      if (!this.autoActive || !this.waitingForInput || this.choiceActive) return;
      this.waitingForInput = false;
      this.hideContinueIndicator();
      const next = this.game.advance();
      if (next) {
        this.processCurrentBeat();
      }
    }, settings.autoAdvanceDelay);
  }

  private clearAutoTimer(): void {
    if (this.autoTimer !== null) {
      clearTimeout(this.autoTimer);
      this.autoTimer = null;
    }
  }

  // ── Status bar & toggle button updates ──

  private updateStatusBar(): void {
    const parts: string[] = [];
    if (this.skipActive) parts.push('SKIP');
    if (this.autoActive) parts.push('AUTO');
    this.statusBar.textContent = parts.join(' | ');
    this.statusBar.style.display = parts.length > 0 ? '' : 'none';
  }

  private updateToggleButtons(): void {
    const skipBtn = this.stage.querySelector('#skip-btn');
    const autoBtn = this.stage.querySelector('#auto-btn');
    if (skipBtn) {
      skipBtn.classList.toggle('active', this.skipActive);
      skipBtn.setAttribute('aria-pressed', String(this.skipActive));
    }
    if (autoBtn) {
      autoBtn.classList.toggle('active', this.autoActive);
      autoBtn.setAttribute('aria-pressed', String(this.autoActive));
    }
  }

  /** Handle keyboard input during choice selection. */
  private handleChoiceKey(e: KeyboardEvent): void {
    const choicesEl = this.stage.querySelector('#choices');
    if (!choicesEl) return;

    const buttons = Array.from(
      choicesEl.querySelectorAll<HTMLButtonElement>('button'),
    );
    if (buttons.length === 0) return;

    switch (e.key) {
      case 'ArrowUp':
      case 'w':
      case 'W':
        e.preventDefault();
        this.choiceFocusIndex =
          (this.choiceFocusIndex - 1 + buttons.length) % buttons.length;
        this.updateChoiceFocus(buttons);
        break;
      case 'ArrowDown':
      case 's':
      case 'S':
        e.preventDefault();
        this.choiceFocusIndex =
          (this.choiceFocusIndex + 1) % buttons.length;
        this.updateChoiceFocus(buttons);
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        buttons[this.choiceFocusIndex]?.click();
        break;
      case '1':
      case '2':
      case '3':
      case '4': {
        const idx = parseInt(e.key, 10) - 1;
        if (idx < buttons.length) {
          e.preventDefault();
          buttons[idx]?.click();
        }
        break;
      }
    }
  }

  /** Update visual focus on choice buttons. */
  private updateChoiceFocus(buttons: HTMLButtonElement[]): void {
    for (let i = 0; i < buttons.length; i++) {
      buttons[i].classList.toggle('focused', i === this.choiceFocusIndex);
      buttons[i].setAttribute('aria-selected', String(i === this.choiceFocusIndex));
    }
    buttons[this.choiceFocusIndex]?.focus();
  }

  /** Handle user input (click / space / enter). */
  private handleGoBack(): void {
    if (this.modalOpen || this.backlogOpen || this.choiceActive) return;
    if (!this.game.canGoBack()) return;

    // Cancel any in-progress typewriter / timers
    this.typewriter?.cancel();
    this.typewriter = null;
    this.clearAutoTimer();
    this.clearSkipTimer();
    if (this.skipActive) this.setSkip(false);
    if (this.autoActive) this.setAuto(false);

    this.waitingForInput = false;
    this.hideContinueIndicator();

    const beat = this.game.goBack();
    if (beat) {
      this.processCurrentBeat();
    }
  }

  /**
   * Walk backwards from the current beat to find and replay the most recent
   * scene beat, restoring background + BGM when loading a saved state.
   */
  private restoreSceneContext(): void {
    const currentIdx = this.script.beats.findIndex(
      (b) => b.id === this.game.state.beatId,
    );
    for (let i = currentIdx - 1; i >= 0; i--) {
      const b = this.script.beats[i];
      if (b.kind === 'scene') {
        this.renderScene(b);
        return;
      }
    }
  }

  private updateBackButton(): void {
    this.backBtn.disabled = !this.game.canGoBack();
  }

  private handleInput(): void {
    // Ignore clicks during choice — buttons handle themselves
    if (this.choiceActive) return;

    // Ignore input while modal is open
    if (this.modalOpen || this.backlogOpen) return;

    // Any manual input disables skip and auto
    if (this.skipActive) this.setSkip(false);
    if (this.autoActive) this.setAuto(false);

    // If typewriter is mid-reveal, complete the line immediately
    if (this.typewriter?.isRevealing()) {
      this.typewriter.complete();
      // Reset lock so user doesn't have to wait again after completing
      this.inputLockUntil = Date.now() + 150;
      return;
    }

    // Spam-click guard: ignore advance input during the lock window
    if (Date.now() < this.inputLockUntil) return;

    // If waiting for input, advance
    if (this.waitingForInput) {
      this.waitingForInput = false;
      this.hideContinueIndicator();
      const next = this.game.advance();
      if (next) {
        this.processCurrentBeat();
      }
    }
  }

  /**
   * Process the current beat. Auto-advancing beats (scene, sfx, setFlag, goto, ifFlag)
   * are processed without waiting for input.
   */
  private processCurrentBeat(): void {
    const beat = this.game.current();

    // Update back button enabled state
    this.updateBackButton();

    // Show beat ID in debug pill (bottom-right corner — tap to copy)
    const pill = document.getElementById('beat-id-pill');
    if (pill) {
      pill.textContent = beat.id;
      pill.title = `Beat ID: ${beat.id} — tap to copy`;
    }

    // Mark beat as read
    this.readBeats.add(beat.id);

    // Update return-to-latest pill visibility
    this.updateReturnPill();

    // Autosave on 'say' and 'choice' beats (user-visible beats)
    if (beat.kind === 'say' || beat.kind === 'choice') {
      this.doAutosave();
    }

    // Preload upcoming assets
    const beatIndex = this.script.beats.findIndex((b) => b.id === beat.id);
    if (beatIndex >= 0) {
      preloadUpcoming(this.script.beats, beatIndex);
    }

    switch (beat.kind) {
      case 'say':
        this.renderSay(beat);
        break;
      case 'scene':
        this.renderScene(beat);
        this.autoAdvance();
        break;
      case 'sfx':
        void AudioEngine.getInstance().playSfx(`./${beat.sfx}`);
        this.autoAdvance();
        break;
      case 'setFlag':
        this.game.setFlag(beat.flag, beat.value);
        this.autoAdvance();
        break;
      case 'goto':
        this.game.jump(beat.goto);
        this.processCurrentBeat();
        break;
      case 'ifFlag':
        this.processIfFlag(beat);
        break;
      case 'choice':
        // Disable skip/auto on choice beats
        if (this.skipActive) this.setSkip(false);
        if (this.autoActive) this.setAuto(false);
        this.renderChoice(beat);
        break;
      case 'end':
        if (this.skipActive) this.setSkip(false);
        if (this.autoActive) this.setAuto(false);
        AudioEngine.getInstance().stopBgm();
        this.renderEnd(beat);
        break;

      case 'wait':
        // Timed pause — auto-advance after duration_ms
        void this.waitMs(beat.duration_ms).then(() => this.autoAdvance());
        break;

      case 'hold':
        // Cinematic hold — may auto-advance or wait for input
        if (beat.allow_skip) {
          this.waitingForInput = true;
        } else {
          void this.waitMs(beat.duration_ms).then(() => this.autoAdvance());
        }
        break;

      case 'thread_aura':
        // Visual thread aura overlay — apply CSS custom property and auto-advance
        document.documentElement.style.setProperty('--thread-aura-color', beat.color);
        document.documentElement.style.setProperty('--thread-aura-intensity', String(beat.intensity));
        if (beat.duration_ms > 0) {
          void this.waitMs(beat.duration_ms).then(() => {
            document.documentElement.style.removeProperty('--thread-aura-color');
            document.documentElement.style.removeProperty('--thread-aura-intensity');
          });
        }
        this.autoAdvance();
        break;

      case 'bond_ring_update':
        // Bond ring HUD update — store in state for UI layer to consume
        document.documentElement.setAttribute('data-bond-threads', beat.visible_threads.join(','));
        document.documentElement.setAttribute('data-bond-mode', beat.mode);
        this.autoAdvance();
        break;

      case 'crest_hud_set':
        // Crest HUD positioning — store as data attributes for UI layer
        document.documentElement.setAttribute('data-crest-position', beat.position);
        document.documentElement.style.setProperty('--crest-opacity', String(beat.opacity));
        document.documentElement.style.setProperty('--crest-scale', String(beat.scale));
        this.autoAdvance();
        break;

      case 'drone_tune':
        // Drone pitch shift — apply to AudioEngine if available (graceful no-op otherwise)
        void this.waitMs(beat.duration_ms).then(() => this.autoAdvance());
        break;
    }
  }

  /** Auto-advance to the next beat without waiting for input. */
  private autoAdvance(): void {
    const next = this.game.advance();
    if (next) {
      this.processCurrentBeat();
    }
  }

  /** Render a 'say' beat: speaker name, portrait, typewriter text. */
  private renderSay(
    beat: Extract<Beat, { kind: 'say' }>,
  ): void {
    this.choiceActive = false;

    // Apply locale overlay
    const localizedBeat = this.game.getCurrentBeatLocalized() as Extract<Beat, { kind: 'say' }>;

    // Speaker name + color (with name_zh support)
    const character = this.game.script.characters[localizedBeat.speaker];
    const locale = this.game.state.locale ?? 'en';
    const speakerName = (locale === 'zh' && character?.name_zh) ? character.name_zh : (character?.name ?? '');
    const speakerColor = character?.color ?? '';

    if (character) {
      this.speakerEl.textContent = speakerName;
      this.speakerEl.style.color = speakerColor;
      this.dialogueEl.classList.remove('narrator');
      // Tint dialogue text with a desaturated version of the character's color
      this.textEl.style.color = this.desaturateColor(speakerColor, 0.35);
    } else {
      // Narrator or unknown speaker
      this.speakerEl.textContent = '';
      this.speakerEl.style.color = '';
      this.dialogueEl.classList.add('narrator');
      this.textEl.style.color = '';
    }

    // Add to backlog
    this.backlog.push({
      speaker: speakerName,
      color: speakerColor,
      text: localizedBeat.text,
    });

    // Update ARIA live region for screen readers
    this.announceToScreenReader(
      speakerName ? `${speakerName}: ${localizedBeat.text}` : localizedBeat.text,
    );

    // Portrait — hide gracefully if asset doesn't exist yet
    if (beat.portrait) {
      this.portraitImg.onerror = () => {
        this.portraitImg.src = '';
        this.portraitLayer.style.display = 'none';
        this.stage.classList.remove('has-portrait');
      };
      this.portraitImg.src = `./${beat.portrait}`;
      this.portraitImg.alt = speakerName ? `Portrait of ${speakerName}` : 'Character portrait';
      this.portraitLayer.style.display = '';
      this.stage.classList.add('has-portrait');
    } else {
      this.portraitImg.src = '';
      this.portraitImg.alt = '';
      this.portraitLayer.style.display = 'none';
      this.stage.classList.remove('has-portrait');
    }

    // BG override on say beat — use parallax plates if available
    if (beat.bg) {
      void this.swapBackground(beat.bg);
    }

    // Show dialogue box
    this.dialogueEl.style.display = '';

    // Cancel any previous typewriter
    this.typewriter?.cancel();

    // Get text speed from settings
    const settings = loadSettings();

    // If skip is active, show text instantly
    if (this.skipActive) {
      this.textEl.textContent = localizedBeat.text;
      this.typewriter = null;
      this.waitingForInput = true;
      this.showContinueIndicator();
      this.doSkipAdvance();
      return;
    }

    // Fire VFX concurrently with typewriter (non-blocking)
    if (beat.fx) {
      void playVfx(beat.fx, this.stage);
    }

    // Start typewriter
    this.typewriter = typewrite(this.textEl, localizedBeat.text, { cps: settings.textSpeed });
    this.typewriter.done.then(() => {
      // Lock input for 400ms after text finishes printing — prevents accidental spam-click skips
      this.inputLockUntil = Date.now() + 400;
      this.waitingForInput = true;
      this.showContinueIndicator();

      // If auto-advance is active, schedule the next advance
      if (this.autoActive) {
        this.scheduleAutoAdvance();
      }
    });
  }

  /** Render a 'scene' beat: swap background (with parallax plates). */
  private renderScene(
    beat: Extract<Beat, { kind: 'scene' }>,
  ): void {
    const transition = beat.transition ?? 'fade';
    void this.swapBackground(beat.bg, transition, beat.bgm);

    // Fire VFX concurrently with scene transition (non-blocking)
    if (beat.fx) {
      void playVfx(beat.fx, this.stage);
    }

    // Hide portrait and dialogue during scene transition
    this.portraitLayer.style.display = 'none';
    this.dialogueEl.style.display = 'none';
  }

  /**
   * Swap background plates with transition.
   * Supports 'fade' (crossfade), 'cut' (instant), and 'dip-to-black'.
   */
  private async swapBackground(
    bgPath: string,
    transition: 'fade' | 'cut' | 'dip-to-black' | 'thread-pull' = 'fade',
    bgmMood?: string,
  ): Promise<void> {
    this.applySceneAtmosphere(bgPath, bgmMood);
    const plates = await probePlates(bgPath);

    if (transition === 'cut') {
      // Instant swap — no animation
      this.applyPlates(plates, bgPath);
      return;
    }

    if (transition === 'dip-to-black') {
      // Fade to black, swap, fade from black
      this.transitionOverlay.classList.add('dip-active');
      await this.waitMs(400);
      this.applyPlates(plates, bgPath);
      // Brief hold on black
      await this.waitMs(200);
      this.transitionOverlay.classList.remove('dip-active');
      return;
    }

    if (transition === 'thread-pull') {
      // Thread-pull: signature THREADBARE transition.
      // The previous scene "pulls away" through a horizontal thread-line
      // before the new scene resolves. Implementation reuses the existing
      // thread-pull module for the visual + a longer pause for gravitas.
      this.transitionOverlay.classList.add('thread-pull-active');
      await this.waitMs(550);
      this.applyPlates(plates, bgPath);
      await this.waitMs(250);
      this.transitionOverlay.classList.remove('thread-pull-active');
      return;
    }

    // Default: crossfade
    // Fade out all layers
    this.bgLayer.style.opacity = '0';
    this.midLayer.style.opacity = '0';
    this.fgLayer.style.opacity = '0';

    // Wait for fade out (250ms matches CSS transition)
    await this.waitMs(250);

    this.applyPlates(plates, bgPath);

    // Fade in. Foreground plates are atmospheric only; keep them subtle so
    // generated full-frame fg assets do not obscure portraits or dialogue.
    this.bgLayer.style.opacity = '1';
    this.midLayer.style.opacity = layerOpacity('midgroundOpacity');
    this.fgLayer.style.opacity = layerOpacity('foregroundOpacity');
  }

  private applySceneAtmosphere(bgPath: string, explicitMood?: string): void {
    const atmosphere = atmosphereForBackground(bgPath);
    this.stage.classList.remove(
      'scene-atmosphere-fraying',
      'scene-atmosphere-memory',
      'scene-atmosphere-night',
      'scene-atmosphere-interior',
      'scene-atmosphere-neutral',
    );
    this.stage.classList.add('scene-atmosphere', `scene-atmosphere-${atmosphere}`);
    this.stage.dataset['atmosphere'] = atmosphere;

    // Drive ambient music from the scene background (or explicit bgm).
    const bgSlug = bgPath.replace(/^assets\/bg\//, '').replace(/\.webp$/, '');
    // If an explicit BGM name is given, use it directly (even if not in AVAILABLE_MOODS —
    // the audio engine will silently no-op if the file doesn't exist yet).
    const mood: SceneMood | string = explicitMood && explicitMood.length > 0
      ? explicitMood
      : resolveSceneMood(bgSlug);
    void AudioEngine.getInstance().playBgm(moodToBgmPath(mood)).then(() => {
      this.updateBgmIndicator();
    });
  }

  /** Apply plate images to the DOM layers. */
  private applyPlates(plates: PlateSet, bgPath: string): void {
    if (plates.far) {
      // 3-layer mode — hide layer gracefully if asset doesn't exist yet
      this.bgFarImg.onerror = () => { this.bgLayer.style.display = 'none'; };
      this.bgFarImg.src = `./${plates.far}`;
      this.bgLayer.style.display = '';
      this.bgLayer.style.opacity = '1';

      if (plates.mid) {
        this.bgMidImg.src = `./${plates.mid}`;
        this.midLayer.style.display = '';
        this.midLayer.style.opacity = layerOpacity('midgroundOpacity');
      } else {
        this.bgMidImg.src = '';
        this.midLayer.style.display = 'none';
      }

      if (plates.fg) {
        this.bgFgImg.src = `./${plates.fg}`;
        this.fgLayer.style.display = '';
        this.fgLayer.style.opacity = layerOpacity('foregroundOpacity');
      } else {
        this.bgFgImg.src = '';
        this.fgLayer.style.display = 'none';
      }
    } else {
      // Legacy single-plate mode — hide gracefully if asset doesn't exist yet
      this.bgFarImg.onerror = () => { this.bgLayer.style.display = 'none'; };
      this.bgFarImg.src = `./${plates.legacy ?? bgPath}`;
      this.bgLayer.style.display = '';
      this.bgLayer.style.opacity = '1';
      this.bgMidImg.src = '';
      this.midLayer.style.display = 'none';
      this.bgFgImg.src = '';
      this.fgLayer.style.display = 'none';
    }
  }

  /** Promise-based delay helper. */
  private waitMs(ms: number): Promise<void> {
    return new Promise((r) => setTimeout(r, ms));
  }

  /** Update the bottom-left BGM indicator with the current track name. */
  private updateBgmIndicator(): void {
    const path = AudioEngine.getInstance().currentBgmPath;
    if (!path) {
      this.bgmIndicator.classList.remove('bgm-indicator-visible');
      this.bgmIndicator.textContent = '';
      return;
    }
    // Convert path like 'public/assets/audio/bgm/looms-theme-slow.mp3'
    // → display name 'looms theme slow'
    const filename = path.split('/').pop() ?? path;
    const name = filename.replace(/\.mp3$/i, '').replace(/-/g, ' ');
    this.bgmIndicator.textContent = `♪ ${name}`;
    this.bgmIndicator.classList.add('bgm-indicator-visible');

    // Fade out after 4 seconds, stay visible on hover
    if (this.bgmIndicatorTimer) clearTimeout(this.bgmIndicatorTimer);
    this.bgmIndicatorTimer = setTimeout(() => {
      this.bgmIndicator.classList.remove('bgm-indicator-visible');
      this.bgmIndicator.classList.add('bgm-indicator-dim');
    }, 4000);
  }

  /** Process an 'ifFlag' beat. */
  private processIfFlag(
    beat: Extract<Beat, { kind: 'ifFlag' }>,
  ): void {
    const flagValue = this.game.state.flags[beat.flag];
    const target = flagValue === beat.value ? beat.goto : beat.else;
    this.game.jump(target);
    this.processCurrentBeat();
  }

  /**
   * Render a 'choice' beat with conditional option filtering,
   * keyboard navigation (↑/↓/W/S, Enter, 1-4), and visual focus.
   */
  private renderChoice(
    _beat: Extract<Beat, { kind: 'choice' }>,
  ): void {
    // Apply locale overlay
    const localizedBeat = this.game.getCurrentBeatLocalized() as Extract<Beat, { kind: 'choice' }>;

    // Show prompt in dialogue
    this.speakerEl.textContent = '';
    this.speakerEl.style.color = '';
    this.textEl.textContent = localizedBeat.prompt;
    this.dialogueEl.style.display = '';
    this.portraitLayer.style.display = 'none';
    this.hideContinueIndicator();

    // Announce choice to screen readers
    this.announceToScreenReader(`Choice: ${localizedBeat.prompt}`);

    // Filter options by flag conditions (use original beat for goto/condition logic)
    const visibleOptions = this.game.getVisibleOptions();

    // Activate choice mode
    this.choiceActive = true;
    this.choiceFocusIndex = 0;

    const choicesDiv = document.createElement('div');
    choicesDiv.className = 'choices';
    choicesDiv.id = 'choices';
    choicesDiv.setAttribute('role', 'listbox');
    choicesDiv.setAttribute('aria-label', localizedBeat.prompt);

    for (let vi = 0; vi < visibleOptions.length; vi++) {
      const opt = visibleOptions[vi];
      const btn = document.createElement('button');
      // Use localized label if available, fallback to original
      btn.textContent = localizedBeat.options[vi]?.label ?? opt.label;
      btn.dataset['optionIndex'] = String(opt.index);
      btn.setAttribute('role', 'option');
      btn.setAttribute('aria-selected', String(vi === 0));
      if (vi === 0) btn.classList.add('focused');

      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.initAudio();
        void AudioEngine.getInstance().playSfx('./assets/audio/sfx/ui-confirm.mp3');
        this.choiceActive = false;
        choicesDiv.remove();
        this.game.applyChoice(opt.index);
        this.processCurrentBeat();
      });

      // Mouse hover updates focus + soft hover click
      btn.addEventListener('mouseenter', () => {
        if (this.choiceFocusIndex !== vi) {
          this.playHoverSfx();
        }
        this.choiceFocusIndex = vi;
        const buttons = Array.from(
          choicesDiv.querySelectorAll<HTMLButtonElement>('button'),
        );
        this.updateChoiceFocus(buttons);
      });

      choicesDiv.appendChild(btn);
    }

    this.dialogueEl.appendChild(choicesDiv);

    // Auto-focus the first button for accessibility
    const firstBtn = choicesDiv.querySelector<HTMLButtonElement>('button');
    firstBtn?.focus();
  }

  /** Render an 'end' beat. */
  private renderEnd(
    beat: Extract<Beat, { kind: 'end' }>,
  ): void {
    this.typewriter?.cancel();
    this.choiceActive = false;
    this.portraitLayer.style.display = 'none';
    this.hideContinueIndicator();

    // Announce ending to screen readers
    this.announceToScreenReader(`End: ${beat.epilogue}`);

    // Show "— fin —" overlay
    this.speakerEl.textContent = '';
    this.textEl.textContent = '';
    this.dialogueEl.style.display = 'none';

    const overlay = document.createElement('div');
    overlay.id = 'end-overlay';
    overlay.setAttribute('role', 'alert');
    overlay.setAttribute('aria-label', 'Game ended');
    overlay.innerHTML = `<div class="end-text">— fin —</div><div class="end-epilogue">${beat.epilogue}</div>`;

    // Add "Return to Title" button
    const returnBtn = document.createElement('button');
    returnBtn.className = 'end-return-btn';
    returnBtn.textContent = 'Return to Title';
    returnBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.returnToTitle();
    });
    overlay.appendChild(returnBtn);

    this.stage.appendChild(overlay);

    // Focus the return button for keyboard users
    requestAnimationFrame(() => returnBtn.focus());

    if (this.onEnd) {
      this.onEnd(beat.epilogue);
    }
  }

  private showContinueIndicator(): void {
    this.continueEl.classList.add('show');
  }

  private hideContinueIndicator(): void {
    this.continueEl.classList.remove('show');
  }

  /** Announce text to screen readers via the ARIA live region. */
  /**
   * Mix a hex color toward white to create a desaturated, readable text tint.
   * blendAmount 0 = original color, 1 = pure white.
   */
  private desaturateColor(hex: string, blendAmount: number): string {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    const mix = (c: number) => Math.round(c + (255 - c) * blendAmount);
    return `rgb(${mix(r)}, ${mix(g)}, ${mix(b)})`;
  }

  private announceToScreenReader(text: string): void {
    // Clear then set to trigger announcement
    this.ariaLive.textContent = '';
    requestAnimationFrame(() => {
      this.ariaLive.textContent = text;
    });
  }

  /** Trigger autosave. */
  private doAutosave(): void {
    autosave(this.game.state, this.script.meta.title);
  }

  /** Open the save modal. */
  private openSaveModal(): void {
    this.modalOpen = true;
    renderSaveLoadModal(this.root, 'save', {
      onLoad: () => { /* not used in save mode */ },
      onClose: () => { this.modalOpen = false; },
      getCurrentState: () => ({
        state: this.game.state,
        scriptTitle: this.script.meta.title,
      }),
    });
  }

  /** Open the load modal. */
  private openLoadModal(): void {
    this.modalOpen = true;
    renderSaveLoadModal(this.root, 'load', {
      onLoad: (state) => {
        this.modalOpen = false;
        this.loadState(state);
      },
      onClose: () => { this.modalOpen = false; },
    });
  }

  /** Open the settings panel. */
  private openSettings(): void {
    this.modalOpen = true;
    renderSettingsPanel(this.root, {
      onClose: () => {
        this.modalOpen = false;
        // Reload settings in case they changed
        this.settings = loadSettings();
        this.parallax?.setEnabled(this.settings.parallaxEnabled ?? true);
      },
      onSettingsChanged: (newSettings) => {
        this.settings = newSettings;
        this.parallax?.setEnabled(newSettings.parallaxEnabled ?? true);
      },
    });
  }

  /** Open the backlog panel. */
  private openBacklog(): void {
    this.backlogOpen = true;
    renderBacklogPanel(this.root, this.backlog, {
      onClose: () => { this.backlogOpen = false; },
    });
  }

  private openSceneSelect(): void {
    if (this.sceneSelectOpen || this.modalOpen || this.backlogOpen) return;
    this.sceneSelectOpen = true;
    renderSceneSelectModal(
      this.root,
      this.game.script,
      this.game.state.visitedSceneIds ?? [],
      {
        onJump: (sceneId) => {
          this.sceneSelectOpen = false;
          this.jumpToScene(sceneId);
        },
        onClose: () => {
          this.sceneSelectOpen = false;
        },
      },
    );
  }

  private jumpToScene(sceneId: string): void {
    this.setSkip(false);
    this.setAuto(false);
    this.game.jump(sceneId);
    this.processCurrentBeat();
    this.updateReturnPill();
  }

  private createReturnPill(): void {
    const pill = document.createElement('button');
    pill.className = 'ss-return-pill';
    pill.textContent = '↩ Return to latest';
    pill.setAttribute('aria-label', 'Return to the furthest scene you have reached');
    pill.style.display = 'none';
    pill.addEventListener('click', () => {
      const latestId = this.game.state.latestBeatId;
      if (latestId) this.jumpToScene(latestId);
    });
    this.stage.appendChild(pill);
    this.returnPill = pill;
  }

  private updateReturnPill(): void {
    if (!this.returnPill) return;
    this.returnPill.style.display = this.game.isAheadOfCurrent() ? 'block' : 'none';
  }

  /** Load a saved state and restart the scene player. */
  private loadState(state: GameState): void {
    this.destroy();
    // Re-create the scene player with the saved state
    new ScenePlayer(
      this.script,
      this.root,
      this.onEnd ?? undefined,
      this.onReturnToTitle ?? undefined,
      state,
    );
  }

  /** Return to the title screen. */
  private returnToTitle(): void {
    this.destroy();
    if (this.onReturnToTitle) {
      this.onReturnToTitle();
    }
  }
}
